import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import has from 'lodash/has';
import isArray from 'lodash/isArray';
import find from 'lodash/find';
import clsx from 'clsx';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import BlockRoundedIcon from '@material-ui/icons/BlockRounded';
import InfoRoundedIcon from '@material-ui/icons/InfoRounded';
import RestoreRoundedIcon from '@material-ui/icons/RestoreRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import { withStyles } from '@material-ui/core/styles';
import Markdown from '@configuratorware/configurator-frontendgui/Framework/Components/Markdown';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import customClassName from '@configuratorware/configurator-frontendgui/App/Utils/customClassName';
import OptionAmount from '../OptionAmount';
import OptionGroup from '@configuratorware/configurator-frontendgui/App/Modules/Creator/Components/OptionGroup/index.js';
import getImageUrl from '@configuratorware/configurator-frontendgui/App/Utils/getImageUrl.js';
import { FormControl, MenuItem, Select } from '@material-ui/core';
import {
  getCurrentOptionFromConfiguration,
  getChairNumberFromConfiguration,
  getSelectedOptionFromConfiguration,
  shouldHidePavillonSizeOptionPrices,
} from '../../Reducers/Configurator/Selectors';
import { useSelector } from 'react-redux';

const styles = (theme) => {
  const textWidthWithAmount =
    theme.creator.option.image.width !== 'auto'
      ? `calc(94% - (${theme.creator.option.image.width} + 35px))`
      : theme.creator.option.textArea.textWidth;

  const customValues = {
    icon: {
      fontSize: '16px',
      lineHeight: 1.6,
    },
    image: {
      fontSize: '11px',
      lineHeight: '11px',
    },
    textArea: {
      fontSize: '0.8em',
      title: {
        fontWeight: 'bold',
      },
    },
  };

  return {
    description: {},
    materialIcon: {},
    price: {},
    details: {
      display: 'inline-block', //!
    },
    iconWithText: {
      lineHeight: customValues.icon.lineHeight,
      display: 'flex',
      verticalAlign: 'middle',
      '& $materialIcon': {
        float: 'left',
        display: 'inline-block',
        paddingRight: '8px',
        minWidth: '20px',
        textAlign: 'center',
      },
    },
    optionList: {
      overflow: 'hidden',
    },
    title: {
      fontWeight: 'bold',
      display: '-webkit-box',
      overflow: 'hidden',
      wordWrap: 'break-word',
      boxOrient: 'vertical',
      lineClamp: 3,
      textOverflow: 'ellipsis',
    },
    image: {
      display: 'inline-block',
      float: 'left',
      height: '100%',
      maxHeight: '100%',
      maxWidth: theme.creator.option.image.width,
      marginRight: '10px',
      fontSize: 0,

      '& img': {
        maxHeight: '100%',
        maxWidth: '100%',
      },
      '& .imageNotAvailableElement': {
        padding: '4px',
        lineHeight: customValues.image.lineHeight,
        fontSize: customValues.image.fontSize,
      },
    },
    root: {
      padding: 10,
      cursor: 'pointer',
      fontSize: customValues.textArea.fontSize,
      display: 'table',
      width: '94%',
      '& .content': {
        position: 'relative',
        display: 'table-cell',
        verticalAlign: 'middle',
        margin: 0,
      },
      '& .disabled': {
        cursor: 'not-allowed',
        opacity: '0.8',
      },
      '&.selected': {
        backgroundColor: '#d8d8d8',
      },
      tapHighlightColor: theme.tapHighlightColor,
    },
    text: {
      float: 'left',
      width: 'calc(94% - ' + theme.creator.option.image.width + ')',
      '& a': {
        textDecoration: 'underline',
      },
      '& $description': {
        marginBottom: 10,
        '& p': {
          margin: 0,
        },
      },
      '& $price': {
        marginBottom: 10,
      },
      '& $title': {
        fontWeight: customValues.textArea.title.fontWeight,
        '& + div': {
          marginTop: '10px',
        },
      },
    },
    withAmount: {
      '& $text': {
        width: textWidthWithAmount,
      },
    },
    optionTextInput: {},
    select: {
      backgroundColor: '#fff',
      borderRadius: 0,
      '& .MuiSelect-icon': {
        color: 'inherit',
      },
      '& .MuiInputBase-input': {
        fontSize: '0.875rem',
        color: '#000',
      },
    },
  };
};

const Option = ({ children, render, ...props }) => {
  const {
    className,
    onClick,
    onSelect,
    onToggle,
    onInputTextChange,
    option,
    optionclassification,
    selected: singleOptionSelected,
    viewDetails,
    viewStockInformation,
    OptionAmountComponent,
    OptionGroupComponent,
    classes,
    isAdminMode,
    isTable,
  } = props;

  const [imageAvailable, setImageAvailable] = useState(true);
  const { selectedoptions } = optionclassification;
  const [inputText, setInputText] = useState(
    get(find(selectedoptions, ['identifier', option.identifier]), 'inputText', '')
  );
  const selectedOption = useSelector((state) =>
    getSelectedOptionFromConfiguration(state, optionclassification.identifier)
  );
  const currentOption = useSelector((state) =>
    getCurrentOptionFromConfiguration(state, optionclassification.identifier, option.identifier)
  );
  const optionGroupSelected =
    selectedOption && option.options?.find(({ identifier }) => identifier === selectedOption.identifier);
  const selected = optionGroupSelected || singleOptionSelected || currentOption?.amount > 0;
  const query = new URLSearchParams(location.search);
  const hidePricesFromQuery = query.has('_hide_prices') && query.get('_hide_prices') === '1';
  const hidePavillonSizeOptionPrices = useSelector((state) =>
    shouldHidePavillonSizeOptionPrices(state, optionclassification.identifier)
  );
  const smallerScreen = useMediaQuery('(min-device-width : 300px) and (max-device-width : 340px)');

  const getDisplayProp = (prop, withFirstOption) =>
    (optionGroupSelected || (withFirstOption && option.options?.[0]) || currentOption)?.[prop] ||
    get(option, prop, '');

  const thumbnail = getDisplayProp('thumbnail', true);
  const optionIdentifier = get(option, 'identifier', '');
  const amountIsSelectable = get(option, 'amount_is_selectable', false);
  const resultStatus = get(option, 'check_results.status', true);
  const optionIsMultiSelect = get(option, 'is_multiselect', false);
  const optionAmount = get(selectedOption, 'amount', 0);
  const hasTextinput = get(option, 'hasTextinput', false);
  const abstract = getDisplayProp('abstract');
  const description = getDisplayProp('description');
  const translations = {
    ...t('common'),
    ...t('optionlistitem'),
  };
  const chairNumber = useSelector((state) => getChairNumberFromConfiguration(state));

  const disabled = optionclassification.identifier === 'cover_model';

  const setImageAvailability = () => {
    setImageAvailable(!imageAvailable);
  };

  const isEnabled = () => {
    return get(option, 'stock.traffic_light', false) !== 'red';
  };

  const shouldDisplayDetails = () => {
    let displayDetails = false;
    if (description) {
      displayDetails = true;
    }

    return displayDetails;
  };

  const onGroupedOptionSelected = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const selectedOption = option.options.find(({ identifier }) => identifier === event.target.value);
    selectedOption &&
      onSelect({
        ...selectedOption,
        amount: 1,
      });
  };

  const onOptionSelected = () => {
    const optionClassificationIsMultiselect = get(optionclassification, 'is_multiselect', false);

    if (amountIsSelectable) {
      if (optionclassification.identifier === 'cover_model') {
        onSelect({
          ...option,
          amount: !chairNumber ? 1 : chairNumber,
        });
      } else {
        onSelect({
          ...option,
          amount: !optionAmount ? 1 : optionAmount,
        });
      }
    } else if (optionClassificationIsMultiselect || isAdminMode) {
      onSelect({
        ...option,
        amount: selected ? 0 : 1,
      });
    } else if (!selected) {
      onSelect({
        ...option,
        amount: 1,
      });
    }
  };

  const onOptionClicked = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (option.options) {
      const firstOption = option.options[0];
      return (
        firstOption &&
        onSelect({
          ...firstOption,
          amount: 1,
        })
      );
    }

    if (isEnabled() && !(hasTextinput && selected)) {
      onOptionSelected();
      onClick();
    }
  };

  const onAmountChanged = (optionAmount) => {
    if (!isEnabled()) {
      return;
    }

    let amount;

    if (amountIsSelectable) {
      amount = optionAmount;
    } else if (optionIsMultiSelect) {
      amount = selected ? 0 : 1;
    } else if (!selected) {
      amount = 1;
    }

    if (amount < 0 || amount > 8) {
      return; // prevent setting below 0 and over 8
    }

    if (optionclassification.identifier === 'cover_model') {
      return; // prevent setting amount for chair_models
    }

    onSelect({
      ...(currentOption || option),
      amount,
    });
  };

  const onAbstractClicked = (event) => {
    event.target.tagName.toLowerCase() === 'a' && event.stopPropagation();
  };

  const onDetailsClicked = (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (isEnabled()) {
      viewDetails(optionGroupSelected || option);
    }
  };

  const onIncompatibilityClicked = (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (isEnabled()) {
      onOptionSelected();
    }
  };

  const onOptionGroupClicked = (event) => {
    event.stopPropagation();
  };

  const onOptionGroupItemSelected = (option) => {
    onSelect({
      ...option,
      amount: 1,
    });
  };

  const onLateDeliveryClicked = (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (isEnabled()) {
      viewStockInformation(option);
    }
  };

  const renderImage = () => {
    const src = getImageUrl(thumbnail);
    return src && <img src={src} onError={setImageAvailability} />;
  };

  const renderNotAvailableImage = () => {
    return (
      <div className={'imageNotAvailableElement'}>
        <Typography variant={'caption'}>{translations.imageNotAvailable}</Typography>
      </div>
    );
  };

  const renderStockStatus = () => {
    if (option.stock) {
      switch (option.stock.traffic_light) {
        case 'red':
          return (
            <div className={classes.iconWithText}>
              <BlockRoundedIcon className={classes.materialIcon} />{' '}
              <Typography variant={'body2'}>{translations.notAvailable}</Typography>
            </div>
          );
        case 'yellow':
          return (
            <div>
              <a className={classes.iconWithText} onClick={onLateDeliveryClicked}>
                <RestoreRoundedIcon className={classes.materialIcon} />{' '}
                <Typography variant={'body2'}>{translations.lateDelivery}</Typography>
              </a>
            </div>
          );
        case 'green':
          return null;
      }
    }
    return null;
  };

  const renderOptionGroup = () => {
    if (!isEnabled()) {
      return null;
    }

    const optionGroup = get(option, 'option_group', {});

    if (has(optionGroup, 'children') && isArray(optionGroup.children) && optionGroup.children.length > 0) {
      return (
        <div onClick={onOptionGroupClicked}>
          <OptionGroupComponent
            itemGroup={optionGroup}
            option={option}
            optionclassification={optionclassification}
            onOptionSelect={onOptionGroupItemSelected}
            selected={selected}
            onToggle={onToggle}
          />
        </div>
      );
    }
    return null;
  };

  const renderActions = () => {
    let displayDetails = shouldDisplayDetails();

    if (displayDetails || !resultStatus) {
      return (
        <div className={classes.details}>
          {displayDetails && (
            <div>
              <a className={classes.iconWithText} onClick={onDetailsClicked}>
                <InfoRoundedIcon
                  className={clsx(classes.materialIcon, customClassName('rule-info-icon-option'))}
                />
                <Typography variant={'body2'} className={classes.iconWithText}>
                  {translations.details}
                </Typography>
              </a>
            </div>
          )}

          {resultStatus !== true && (
            <div>
              <a className={classes.iconWithText} onClick={onIncompatibilityClicked}>
                <WarningRoundedIcon
                  className={clsx(classes.materialIcon, customClassName('rule-alert-icon-option'))}
                />
                <Typography variant={'body2'}>{translations.incompatibility}</Typography>
              </a>
            </div>
          )}

          {renderStockStatus()}

          {renderOptionGroup()}
        </div>
      );
    }
  };

  const renderText = () => {
    return (
      <Grid item xs={12}>
        <Typography component={'div'} className={classes.optionList} variant={'body2'}>
          <div className={classes.title}>{option.title}</div>
          {hasTextinput && renderTextInput()}
          {!hidePricesFromQuery && !isTable && !hidePavillonSizeOptionPrices && (
            <div className={classes.price}>
              {currentOption ? currentOption.priceFormatted : option.priceFormatted}
            </div>
          )}
          {smallerScreen && renderControls('horizontal')}
          {abstract && (
            <Markdown className={classes.description} onClick={onAbstractClicked}>
              {abstract}
            </Markdown>
          )}
          {optionGroupSelected && (
            <FormControl fullWidth size="small">
              <Select
                id="select"
                className={classes.select}
                value={selectedOption?.identifier || ''}
                onChange={onGroupedOptionSelected}
                variant={'outlined'}
              >
                {renderDropDown(option.options)}
              </Select>
            </FormControl>
          )}
          {renderActions()}
        </Typography>
      </Grid>
    );
  };

  const renderDropDown = (options) => {
    return options.map((o) => (
      <MenuItem key={o.identifier} value={o.identifier}>
        {o.title}
      </MenuItem>
    ));
  };

  const renderControls = (view) => {
    if (isEnabled() && amountIsSelectable) {
      return (
        <OptionAmountComponent
          view={view}
          option={currentOption || option}
          onAmountChange={onAmountChanged}
          onSelect={onSelect}
          disabled={disabled}
        />
      );
    } else {
      return null;
    }
  };

  const handleTextInputChange = (event) => {
    setInputText(event.target.value);
    onInputTextChange(option.identifier, event.target.value);
  };

  const renderTextInput = () => {
    return (
      <TextField
        className={classes.optionTextInput}
        label={''}
        value={inputText}
        onChange={handleTextInputChange}
        disabled={!selected}
      />
    );
  };

  const getRootClassName = () => clsx(classes.root, selected && 'selected', customClassName('option'));

  const getContentClassName = () => {
    const amountClass = amountIsSelectable && !smallerScreen ? classes.withAmount : '';
    return clsx(
      'content',
      className,
      optionIdentifier,
      amountClass,
      !isEnabled() && 'disabled',
      resultStatus !== true && 'incompatible'
    );
  };

  const getRenderProps = () => ({
    ...props,
    imageAvailable,
    smallerScreen,
    renderImage,
    renderNotAvailableImage,
    renderText,
    renderControls,
    renderActions,
    renderOptionGroup,
    renderStockStatus,
    setImageAvailability,
    isEnabled,
    shouldDisplayDetails,
    onOptionSelected,
    onOptionClicked,
    onAmountChanged,
    onAbstractClicked,
    onDetailsClicked,
    onIncompatibilityClicked,
    onOptionGroupClicked,
    onOptionGroupItemSelected,
    onLateDeliveryClicked,
    getRootClassName,
    getContentClassName,
  });

  const renderItem = () => {
    return (
      <div className={getRootClassName()} data-option={option.identifier} onClick={onOptionClicked}>
        <div className={getContentClassName()}>
          {typeof children === 'function' ? (
            children(getRenderProps())
          ) : (
            <>
              <div className={classes.image}>
                {imageAvailable ? renderImage() : renderNotAvailableImage()}
              </div>

              <div className={classes.text}>{renderText()}</div>

              {!smallerScreen && renderControls()}
            </>
          )}
        </div>
      </div>
    );
  };

  return typeof render === 'function' ? render(getRenderProps()) : renderItem();
};

Option.defaultProps = {
  className: '',
  onClick: () => {},
  onSelect: () => {},
  onToggle: () => {},
  onInputTextChange: () => {},
  optionclassification: {},
  option: {},
  selected: false,
  translations: {},
  viewDetails: () => {},
  viewStockInformation: () => {},
  OptionAmountComponent: OptionAmount,
  OptionGroupComponent: OptionGroup,
};

Option.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  onSelect: PropTypes.func,
  onToggle: PropTypes.func,
  onInputTextChange: PropTypes.func,
  option: PropTypes.object,
  optionclassification: PropTypes.object,
  selected: PropTypes.bool,
  translations: PropTypes.object,
  viewDetails: PropTypes.func,
  viewStockInformation: PropTypes.func,
  OptionAmountComponent: PropTypes.elementType,
  OptionGroupComponent: PropTypes.elementType,
  isAdminMode: PropTypes.bool,
  children: PropTypes.func,
  render: PropTypes.func,
};

export default withStyles(styles, { name: 'Option' })(Option);
